import { motion } from "framer-motion";
import { Star, Shield, Sparkles, CheckCircle, Phone, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuoteForm } from "@/components/QuoteFormContext";
import { siteConfig } from "@/lib/site-config";
import FormEmbed from "@/components/FormEmbed";

const badges = [
  { icon: Shield, label: "Licensed & Insured" },
  { icon: Star, label: "5-Star Service" },
  { icon: Clock, label: "Fast Turnaround" },
  { icon: CheckCircle, label: "Free Estimates" },
];

const stats = [
  { value: "14", label: "Cities Served", suffix: "+" },
  { value: "24", label: "Online Support", suffix: "/7" },
  { value: "100", label: "Free Quotes", suffix: "%" },
];

const HeroSection = () => {
  const { openQuoteForm } = useQuoteForm();

  return (
    <section
      id="home"
      className="relative min-h-[100svh] flex items-center overflow-hidden"
    >
      <img
        src="https://sbdjxsxpkgngbkdabugx.supabase.co/storage/v1/render/image/public/client-media/inka-glass-llc/images/1781792848038-339-34921.jpg?width=1600&height=900&resize=cover&quality=62"
        srcSet="https://sbdjxsxpkgngbkdabugx.supabase.co/storage/v1/render/image/public/client-media/inka-glass-llc/images/1781792848038-339-34921.jpg?width=828&height=466&resize=cover&quality=60 828w, https://sbdjxsxpkgngbkdabugx.supabase.co/storage/v1/render/image/public/client-media/inka-glass-llc/images/1781792848038-339-34921.jpg?width=1600&height=900&resize=cover&quality=62 1600w"
        sizes="100vw"
        alt="Professional window and glass replacement in Houston, TX"
        className="absolute inset-0 w-full h-full object-cover"
        loading="eager"
        fetchPriority="high"
        decoding="async"
        width={1600}
        height={900}
      />
      <div className="absolute inset-0 hero-overlay" />
      <div className="absolute inset-0 bg-gradient-to-br from-[hsl(220,60%,8%/0.78)] via-[hsl(220,60%,8%/0.65)] to-[hsl(205,70%,20%/0.45)]" />
      <div className="absolute inset-0 gradient-mesh opacity-40" />

      <div
        className="absolute top-20 right-20 w-96 h-96 rounded-full hidden md:block animate-blob"
        style={{ background: "radial-gradient(circle, hsl(var(--primary) / 0.35), transparent 70%)" }}
        aria-hidden="true"
      />
      <div
        className="absolute bottom-40 left-10 w-64 h-64 rounded-full hidden md:block animate-blob"
        style={{
          background: "radial-gradient(circle, hsl(var(--secondary) / 0.4), transparent 70%)",
          animationDelay: "2s",
          animationDuration: "9s",
        }}
        aria-hidden="true"
      />

      <div className="relative z-10 container-max px-4 pt-20 pb-32 md:pt-24 md:pb-32 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ y: 16 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            >
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.15, duration: 0.5 }}
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 text-white text-xs sm:text-sm font-semibold px-4 sm:px-5 py-2 rounded-full mb-6 shadow-lg"
              >
                <Sparkles className="h-4 w-4 text-secondary" />
                <span className="text-white">Greater Houston Area · Free Estimates</span>
              </motion.div>

              <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white leading-[1.05] mb-5 tracking-tight font-bold drop-shadow-[0_4px_24px_rgba(0,0,0,0.45)]">
                New Windows.
                <br />
                <span className="text-gradient-primary">Flawless</span> Glass.
              </h1>

              {/* Readable tagline with glass background card */}
              <div className="inline-block bg-[#0a0f1f]/55 backdrop-blur-md border border-white/15 rounded-2xl px-5 py-4 mb-8 max-w-xl shadow-xl">
                <p className="text-white text-base md:text-lg leading-relaxed">
                  <span className="font-bold text-white">Inka Glass LLC</span> provides professional
                  window replacement and glass replacement for homes and businesses across Houston, TX
                  and surrounding areas — energy-efficient, precisely fit, and built for the Texas climate.
                </p>
              </div>

              <div className="flex flex-wrap gap-3 mb-8">
                <Button
                  size="lg"
                  onClick={openQuoteForm}
                  className="text-base gap-2 px-7 py-6 rounded-xl font-semibold shadow-xl"
                >
                  Get Free Estimate
                </Button>
                <Button
                  asChild
                  variant="outline"
                  size="lg"
                  className="text-base gap-2 px-7 py-6 rounded-xl border-2 border-white/30 text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm font-semibold"
                >
                  <a href={siteConfig.phoneHref}>
                    <Phone className="h-5 w-5" /> {siteConfig.phone}
                  </a>
                </Button>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.5 }}
                className="glass-card rounded-xl px-5 py-3 inline-flex items-center gap-3"
              >
                <span className="text-2xl">🪟</span>
                <span className="text-white font-semibold text-sm">
                  <span className="text-secondary font-bold">Free Estimates</span> · Residential & Commercial Glass
                </span>
              </motion.div>
            </motion.div>
          </div>

          {/* Form widget — exactly as currently positioned, right-side hero, original dimensions preserved */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="lg:col-span-5"
          >
            <div className="widget-shell bg-white">
              <div className="px-6 pt-6 pb-2">
                <div className="font-heading text-2xl text-foreground font-bold tracking-tight">
                  Get Your Free Estimate
                </div>
                <p className="text-muted-foreground text-sm mt-1">
                  No pressure, no obligation — just honest pricing.
                </p>
              </div>
              <FormEmbed className="p-4 sm:p-6" />
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.5 }}
          className="mt-10 lg:mt-12 glass-card rounded-2xl p-5 md:p-6 gradient-border"
        >
          <div className="flex flex-wrap items-center justify-around gap-5">
            {stats.map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 + i * 0.15, duration: 0.5 }}
                className="text-center"
              >
                <div className="font-heading text-3xl md:text-4xl text-white tracking-wide font-bold">
                  {stat.value}<span className="text-secondary">{stat.suffix}</span>
                </div>
                <div className="text-white/90 text-xs font-medium uppercase tracking-wider mt-1">{stat.label}</div>
              </motion.div>
            ))}
            {badges.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2">
                <div className="h-9 w-9 rounded-lg bg-secondary/25 flex items-center justify-center shrink-0">
                  <Icon className="h-4 w-4 text-secondary" />
                </div>
                <span className="text-white/85 text-xs sm:text-sm font-medium">{label}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Concave/curved bottom edge */}
      <div className="pointer-events-none absolute -bottom-px left-0 right-0 h-20 md:h-24 leading-none">
        <svg
          viewBox="0 0 1440 96"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="block w-full h-full"
          preserveAspectRatio="none"
        >
          <path
            d="M0 0C240 96 480 96 720 64C960 32 1200 32 1440 64V96H0V0Z"
            fill="hsl(var(--background))"
          />
        </svg>
      </div>
    </section>
  );
};

export default HeroSection;
